import React, { ReactNode, useMemo, useState } from "react";
import {
    Badge,
    Box,
    Button,
    HStack,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from "@chakra-ui/react";

export type FinanceDataTableColumn<T> = {
    key: string;
    header: ReactNode;
    render: (row: T, index: number) => ReactNode;
    isNumeric?: boolean;
    width?: string;
};

type FinanceDataTableProps<T> = {
    rows: T[];
    columns: Array<FinanceDataTableColumn<T>>;
    getRowKey: (row: T, index: number) => string;
    emptyText?: string;
    initialPageSize?: number;
    pageSizeOptions?: number[];
    showPagination?: boolean;
    showViewMore?: boolean;
    tableSize?: "sm" | "md" | "lg";
};

const FinanceDataTable = <T,>({
    rows,
    columns,
    getRowKey,
    emptyText = "No records found.",
    initialPageSize = 10,
    pageSizeOptions = [10, 25, 50, 100],
    showPagination = true,
    showViewMore = true,
    tableSize = "sm",
}: FinanceDataTableProps<T>) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);

    const totalRows = rows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const safePage = Math.min(page, totalPages);

    const visibleRows = useMemo(() => {
        if (!showPagination) return rows;

        const start = (safePage - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [pageSize, rows, safePage, showPagination]);

    const canGoPrevious = safePage > 1;
    const canGoNext = safePage < totalPages;

    const handlePageSizeChange = (value: number) => {
        setPageSize(value);
        setPage(1);
    };

    const handleViewMore = () => {
        setPageSize((current) => current + initialPageSize);
        setPage(1);
    };

    if (!rows.length) {
        return (
            <Box
                p={5}
                borderRadius="16px"
                border="1px dashed"
                borderColor="gray.300"
                _dark={{ borderColor: "gray.700" }}
            >
                <Text color="gray.500">{emptyText}</Text>
            </Box>
        );
    }

    return (
        <Box>
            <Box overflowX="auto">
                <Table size={tableSize}>
                    <Thead>
                        <Tr>
                            {columns.map((column) => (
                                <Th
                                    key={column.key}
                                    isNumeric={column.isNumeric}
                                    width={column.width}
                                    whiteSpace="nowrap"
                                >
                                    {column.header}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>

                    <Tbody>
                        {visibleRows.map((row, index) => (
                            <Tr key={getRowKey(row, index)}>
                                {columns.map((column) => (
                                    <Td
                                        key={column.key}
                                        isNumeric={column.isNumeric}
                                        verticalAlign="top"
                                    >
                                        {column.render(row, index)}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {showPagination && (
                <HStack
                    mt={4}
                    spacing={3}
                    justify="space-between"
                    align="center"
                    flexWrap="wrap"
                >
                    <HStack spacing={2}>
                        <Badge variant="subtle">
                            Showing {visibleRows.length} of {totalRows}
                        </Badge>

                        <Text fontSize="sm" color="gray.500">
                            Page {safePage} of {totalPages}
                        </Text>
                    </HStack>

                    <HStack spacing={2} flexWrap="wrap">
                        <Select
                            size="sm"
                            value={pageSize}
                            maxW="110px"
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        >
                            {pageSizeOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}/page
                                </option>
                            ))}
                        </Select>

                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            isDisabled={!canGoPrevious}
                        >
                            Previous
                        </Button>

                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                setPage((current) => Math.min(totalPages, current + 1))
                            }
                            isDisabled={!canGoNext}
                        >
                            Next
                        </Button>

                        {showViewMore && totalRows > visibleRows.length && (
                            <Button size="sm" onClick={handleViewMore}>
                                View more
                            </Button>
                        )}
                    </HStack>
                </HStack>
            )}
        </Box>
    );
};

export default FinanceDataTable;